import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { soapRuleAPI } from '@/features/internal/soap-rules/apis/soapRule'
import { SoapRule } from '@/features/internal/soap-rules/types/soapRule'
import { useToast } from '@/shared/contexts/ToastContext'

/**
 * SOAPルール一覧取得クエリ (作成順 = 適用順)
 */
export const useSoapRulesQuery = () => {
  return useQuery({
    queryKey: queryKeys.soapRules.all,
    queryFn: async (): Promise<SoapRule[]> => {
      return await soapRuleAPI.getRules()
    },
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })
}

export const useCreateSoapRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      rule: Omit<SoapRule, 'id' | 'created_at'>
    ): Promise<SoapRule> => {
      return await soapRuleAPI.createRule(rule)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.soapRules.all })
      showToast('ルールを作成しました', 'success')
    },
    onError: () => {
      showToast('ルールの作成に失敗しました', 'error')
    },
  })
}

export const useUpdateSoapRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      id,
      rule,
    }: {
      id: string
      rule: Partial<SoapRule>
    }): Promise<SoapRule> => {
      return await soapRuleAPI.updateRule(id, rule)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.soapRules.all })
      showToast('ルールを更新しました', 'success')
    },
    onError: () => {
      showToast('ルールの更新に失敗しました', 'error')
    },
  })
}

export const useDeleteSoapRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await soapRuleAPI.deleteRule(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.soapRules.all })
      showToast('ルールを削除しました', 'success')
    },
    onError: () => {
      showToast('ルールの削除に失敗しました', 'error')
    },
  })
}
